import {
  FilteredMaterialsByCategoriesI,
  IProductConfigurationResult,
  Material,
  ProductData,
  VariantData,
} from "ecom-types";

export type IUsePdpFilterOptions = {
  globalsSortOrder?: boolean;
};

export function usePdpFilter (
  productData: Ref<ProductData | null>,
  currentConfiguration: Ref<VariantData | null>,
  sizesSlugs: string[] = [],
  options?: IUsePdpFilterOptions
) {
  const _options = options || {};
  const result = ref<IProductConfigurationResult>({});
  const additionalOptions = ref<IProductConfigurationResult>({});
  const clickOnAnyFilter = ref(false);

  const route = useRoute();
  const router = useRouter();
  const generalStore = useGeneralStore();

  const isDefaultSize = computed(() => {
    return route.query.size === "default";
  });

  const filteredMaterialsByCategories = computed(
    (): FilteredMaterialsByCategoriesI[] => {
      if (!productData.value) {
        return [];
      }

      const materialsByCategories = productData.value.material_categories.reduce(
          (acc: FilteredMaterialsByCategoriesI[], el) => {
            const currentCategory =
                generalStore.bootstrap?.material_categories?.find(
                    (it) => it.id === el.id,
                );

            if (currentCategory) {
              acc.push({
                ...currentCategory,
                materials: productData.value!.materials.reduce(
                    (acc: Material[], material) => {
                      if (material.material_category_id === el.id) {
                        acc.push({
                          ...material,
                        });
                      }
                      return acc;
                    },
                    [],
                ),
              });
            }
            return acc;
          },
          [],
      )

      return _options.globalsSortOrder ?
          materialsByCategories.sort((a, b) => (a?.sort_order || 0) - (b?.sort_order || 0)):
          materialsByCategories;
    },
  );

  const disabledMaterials = computed(() => {
    if (!productData.value) return [];
    const disabledMaterialsSet = new Set();
    const selectedMaterialsSet = new Set(
      [
        ...Object.values(result.value),
        ...Object.values(additionalOptions.value),
      ]
        .filter(Boolean)
        .map((material) => material?.id),
    );

    productData.value.materials_disabled_pairs.forEach((pair) => {
      // Uncomment for two-way disabled
      // if (selectedMaterialsSet.has(pair.material_with_id)) {
      //   disabledMaterialsSet.add(pair.material_id);
      // }
      if (selectedMaterialsSet.has(pair.material_id)) {
        disabledMaterialsSet.add(pair.material_with_id);
      }
    });

    return Array.from(disabledMaterialsSet);
  });

  const allSelectedMaterials = computed(() => {
    return {
      ...result.value,
      ...additionalOptions.value,
    };
  });

  const hasUnavailableMaterial = computed(() => {
    return [
      ...Object.values(result.value),
      ...Object.values(additionalOptions.value),
    ]
      .filter(Boolean)
      .some((material) => disabledMaterials.value.includes(material?.id));
  });

  const filteredMaterialsByCategoriesWithDisabled = computed(() => {
    return filteredMaterialsByCategories.value.map((category) => {
      return {
        ...category,
        materials: category.materials.map((material) => ({
          ...material,
          disabled: disabledMaterials.value.includes(material.id),
        })),
      };
    });
  });

  const materialsQuery = computed({
    get(): string[] {
      if (Array.isArray(route.query.materials)) {
        return (route.query.materials as string[]) || [];
      } else if (route.query.materials) {
        return route.query.materials
          ? ([route.query.materials] as string[])
          : [];
      } else {
        return (
          (Object.values(additionalOptions.value)?.map(
            (el) => el?.sku,
          ) as string[]) || []
        );
      }
    },
    async set(val: string | string[]) {
      await router.replace({
        path: route.path,
        query: {
          ...route.query,
          materials: val,
        },
      });
    },
  });

  const filtersPDPManyInOne = computed(() => {
    return filteredMaterialsByCategories.value?.some(
      (el) => el?.materials?.length > 1,
    );
  });

  async function onFilterClick(categorySlug: string, item: any) {
    if (!item) return;
    if (isDefaultSize.value && sizesSlugs.includes(categorySlug)) {
      await router.replace({ query: { ...route.query, size: undefined } });
    }
    clickOnAnyFilter.value = true;

    result.value[categorySlug] = item;
    checkAvailabilityMaterials();
    await setConfigurationValues();
  }

  async function setConfigurationValues() {
    const newSku = Object.values(result.value).reduce((acc, el: any) => {
      return acc + "-" + el.sku;
    }, productData.value?.vendor_model as string);

    const materials = Object.values(additionalOptions.value).reduce(
      (acc, value) => {
        if (value) {
          acc.push(value.sku);
        }
        return acc;
      },
      [] as string[],
    );

    await router.replace({
      path: route.path,
      query: {
        ...route.query,
        configuration_id: newSku,
        materials,
      },
    });
  }

  async function onOptionsFilterClick(categorySlug: string, item: any) {
    if (!item) return;
    if (isDefaultSize.value && sizesSlugs.includes(categorySlug)) {
      await router.replace({ query: { ...route.query, size: undefined } });
    }
    clickOnAnyFilter.value = true;
    additionalOptions.value[categorySlug] = item;
    checkAvailabilityMaterials();
    await setConfigurationValues();
  }

  function checkAvailabilityMaterials() {
    function updateAvailability(results: IProductConfigurationResult) {
      Object.entries(results).forEach(([key, value]) => {
        if (value) {
          const materialCategory =
            filteredMaterialsByCategoriesWithDisabled.value.find(
              (category) => category.id === value.material_category_id,
            );
          const foundMaterial = materialCategory?.materials.find(
            (material) => material.id === value.id,
          );
          if (foundMaterial?.disabled) {
            const newValue = materialCategory?.materials.find(
              (material) => !material.disabled,
            );
            if (newValue) {
              results[key] = newValue;
            }
          }
        }
      });
    }

    updateAvailability(result.value);
    updateAvailability(additionalOptions.value);
  }

  async function setResultData() {
    productData.value?.material_categories.forEach((el) => {
      const currentMaterials = filteredMaterialsByCategories.value?.length
        ? filteredMaterialsByCategories.value?.find(
            (item) => item.id === el?.id,
          )?.materials
        : [];
      const material = currentMaterials?.find((item) =>
        currentConfiguration.value?.materials?.includes(item.id),
      );
      if (el?.slug && material && el.sku_enabled) {
        result.value[el?.slug] = material;
      } else {
        const currentItems = currentMaterials?.find(
          (el) =>
            (materialsQuery.value as string[])?.some(
              (i: string) => i === el.sku,
            ) || null,
        );

        if (currentItems || currentMaterials?.[0]) {
          if (sizesSlugs.includes(el.slug)) {
            additionalOptions.value[el?.slug] = currentItems || null;
          } else {
            additionalOptions.value[el?.slug] =
              currentItems || currentMaterials?.[0] || null;
          }
        }
      }
    });
    checkAvailabilityMaterials();
    await setConfigurationValues();
  }

  return {
    result,
    additionalOptions,
    clickOnAnyFilter,
    isDefaultSize,
    filteredMaterialsByCategories,
    disabledMaterials,
    hasUnavailableMaterial,
    filteredMaterialsByCategoriesWithDisabled,
    materialsQuery,
    filtersPDPManyInOne,
    onFilterClick,
    setConfigurationValues,
    onOptionsFilterClick,
    checkAvailabilityMaterials,
    setResultData,
    allSelectedMaterials,
  };
};
